import { CadNextNotificationPriorityEnum } from '@mark43/cad-api';
import {
    useAllEventNotificationsForEntityTypeForEvent,
    useEventNotificationsStore,
} from './use-event-notifications';
import { useEventNotificationsEventId } from './event-notifications-event-id-context';
import {
    AdditionalInfoNotificationManager,
    AdditionalInfoView,
    Priority,
    UnreadInfo,
} from '../additional-infos/types';
import { sleep } from '@m43/core';

export function useCadNextAdditionalInfoNotifications(): AdditionalInfoNotificationManager {
    const linkedEntityId = useEventNotificationsEventId();

    const notifs = useAllEventNotificationsForEntityTypeForEvent({
        eventId: linkedEntityId,
        entityType: 'ADDITIONAL_INFO',
    });

    const sortedNotifs = [...notifs].sort((n1, n2) => n1.entityId - n2.entityId);
    const store = useEventNotificationsStore();

    const getUnreadInfoForPriority = (priority: Priority): UnreadInfo | null => {
        const isHighPriority = priority === 'HIGH_PRIORITY_UPDATE';
        const priorityValue = isHighPriority
            ? CadNextNotificationPriorityEnum.HIGH
            : CadNextNotificationPriorityEnum.STANDARD;

        const unreadNotification = sortedNotifs.find(
            (n) => n.notificationPriority === priorityValue.name
        );

        return unreadNotification ? { id: unreadNotification.entityId, priority } : null;
    };

    const unreadInfos = {
        [Priority.STANDARD]: getUnreadInfoForPriority(Priority.STANDARD),
        [Priority.HIGH]: getUnreadInfoForPriority(Priority.HIGH),
    };

    const earliestUnreadInfoId = sortedNotifs[0]?.entityId;

    const hasUnread = notifs.length !== 0;
    const markAsRead = async (infos: AdditionalInfoView[], immediate = false): Promise<void> => {
        if (!immediate) {
            await sleep(3000);
        }

        if (linkedEntityId) {
            store.removeAllNotificationsForEntityTypeForEvent(linkedEntityId, 'ADDITIONAL_INFO');
        }
    };

    return {
        hasUnread,
        unreadInfos,
        markAsRead,
        earliestUnreadInfo: earliestUnreadInfoId ? { id: earliestUnreadInfoId } : null,
    };
}
